'use client';

import type { Address } from '@morpho-org/blue-sdk';
import type { InterfaceToken } from '@morpho-org/dapp-core';
import type { ActionRequirement } from '@morpho-org/morpho-sdk';
import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { DEFAULT_SLIPPAGE_TOLERANCE, MathLib } from '@morpho-org/blue-sdk';
import { vaultV2Abi } from '@morpho-org/blue-sdk-viem';
import { isRequirementSignature } from '@morpho-org/morpho-sdk';
import { NotificationStatus, useToastContext } from '@morpho-org/ui-v6/contexts';
import { ensureError, withTimeout } from '@repo/utils';
import { type ActionFlowResult, useActionFlow, useWalletSupportsAtomicBatching } from '@repo/web3';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { erc20Abi, formatUnits, isAddressEqual } from 'viem';
import { useWalletClient } from 'wagmi';
import { getBalanceQueryKey, readContractQueryKey } from 'wagmi/query';
import type { ActionFlowExit } from '@/components/ActionFlow/ActionFlowReviewDialog';
import type { CheckoutEntryPoint } from '@/lib/analytics/client/events';
import { ActionFlowReviewDialog } from '@/components/ActionFlow/ActionFlowReviewDialog';
import { useActionFlowRegistration } from '@/components/ActionFlow/useActionFlowRegistration';
import { useVaultV2RecapData, type VaultV2RecapSource } from '@/components/Bundler/ConfirmDialog/Recaps/hooks/useVaultV2RecapData';
import VaultV2Recap from '@/components/Bundler/ConfirmDialog/Recaps/VaultV2Recap';
import { ErrorServiceUnavailable } from '@/components/common/Error/ErrorServiceUnavailable';
import SectionErrorBoundary from '@/components/common/SectionErrorBoundary';
import { useVaultV2SimulatedFinalPosition } from '@/components/VaultPage/VaultV2/hooks/useVaultV2SimulatedFinalPosition';
import { useWeb3Context } from '@/contexts/Web3Context';
import { formatTransactionRequirement } from '@/helpers/requirement';
import { safeSentryBreadcrumb } from '@/helpers/sentry';
import { getWalletProvider } from '@/helpers/walletProvider';
import { invalidateUserEarnPortfolio } from '@/hooks/positions/invalidateUserEarnPortfolio';
import { buildActionFlow } from '@/lib/action-flow/buildActionFlow';
import { getMorphoSdkActionFlowSource, type VaultActionRequirement } from '@/lib/action-flow/morphoSdkActionFlowSource';
import { waitForApiSync } from '@/lib/action-flow/waitForApiSync';
import { type ActionFlowTracking, txSourceForTracking } from '@/lib/analytics/client/trackers/action-flow';
import { buildReviewDialogParams } from '@/lib/analytics/client/trackers/checkout-funnel';
import { trackVaultDepositCompleted } from '@/lib/analytics/client/trackers/vault-deposit';
import { getWalletType } from '@/lib/observability/shared/wallet-type';
import { orpc } from '@/lib/orpc/client';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import { getTxFlowTracking } from '@/lib/shared/utils/flags.constants';
import { InterfaceOperation } from '@/operations/operations.types';
import { checkSimulation, toSimulationTx } from '@/services/simulate/simulate';
import { tryBuildSimulationAuthorizations } from '@/services/simulate/simulatedPosition.helpers';
import { buildSimulationLimits } from '@/services/simulate/simulationLimits';
import { getReviewErrorReason } from './reviewErrorReason';
import { useVaultV2DepositTransaction } from './useVaultV2DepositTransaction';
type DepositRecapSnapshot = {
  initialPosition: bigint;
  finalPosition: bigint | undefined;
  isSimulationFallback: boolean;
};
function useDepositRecapSnapshot(isIdle, initialPosition, finalPosition, isSimulationFallback) {
  const [snapshot, setSnapshot] = useState();
  if (isIdle && snapshot !== undefined) {
    setSnapshot(undefined);
  } else {
    if (!isIdle && snapshot === undefined) {
      setSnapshot({
        initialPosition,
        finalPosition,
        isSimulationFallback
      });
    }
  }
  return snapshot;
}
function isDepositSimulationPreparing(isLoading: boolean, hasAttemptedExecution: boolean) {
  return isLoading || hasAttemptedExecution;
}
function isDepositActionRefreshing({
  isRefreshing,
  hasAttemptedCurrentAmount,
  isSimulationRefreshing,
  simulationStatus
}: {
  isRefreshing: boolean;
  hasAttemptedCurrentAmount: boolean;
  isSimulationRefreshing: boolean;
  simulationStatus: string;
}) {
  return isRefreshing || !hasAttemptedCurrentAmount && (isSimulationRefreshing || simulationStatus === 'loading');
}
export interface VaultV2DepositActionFlowReviewDialogProps {
  vaultAddress: Address;
  inputAmount: bigint;
  erc20Amount: bigint;
  nativeAmount: bigint;
  vaultAsset: InterfaceToken;
  userPosition: bigint;
  finalPosition: bigint | undefined;
  onTxSuccess: () => void;
  refetchPosition: () => Promise<unknown>;
  onExit: (exit: ActionFlowExit) => void;
  entryPoint: CheckoutEntryPoint;
  recapSource?: VaultV2RecapSource;
  refetchVaultData: () => Promise<unknown>;
}
export function VaultV2DepositActionFlowReviewDialog({
  vaultAddress,
  inputAmount,
  erc20Amount,
  nativeAmount,
  vaultAsset,
  userPosition,
  finalPosition,
  onTxSuccess,
  refetchPosition,
  refetchVaultData,
  onExit,
  entryPoint,
  recapSource
}: VaultV2DepositActionFlowReviewDialogProps) {
  const flags = useFeatureFlags();
  const queryClient = useQueryClient();
  const {
    callRequests,
    state
  } = useActionFlow();
  const {
    displayTxToast
  } = useToastContext();
  const {
    registerActionFlow,
    canReplace
  } = useActionFlowRegistration();
  const {
    data: client,
    isLoading: isClientLoading
  } = useWalletClient();
  const {
    targetChain,
    account
  } = useWeb3Context();
  const address = account.address;
  const recapData = useVaultV2RecapData({
    vaultAddress,
    source: recapSource
  });
  const formGeneration = useRef(0);
  const pendingSyncResult = useRef<ActionFlowResult | undefined>(undefined);
  const trackDepositCompletedRef = useRef<(() => void) | undefined>(undefined);
  const bypassFinalSimulation = useRef(false);
  const userBypassedSimulation = useRef(false);
  const [attemptedAmountKey, setAttemptedAmountKey] = useState<string>();
  const [finalSimulationError, setFinalSimulationError] = useState<unknown>();
  const connectedAddress = useRef(address);
  useEffect(() => {
    connectedAddress.current = address;
  }, [address]);
  useEffect(() => () => {
    formGeneration.current += 1;
  }, []);
  const [reviewSessionId] = useState(() => crypto.randomUUID());
  const capability = useWalletSupportsAtomicBatching(address, targetChain.id, {
    enabled: !!address
  });
  const {
    transaction,
    requirements,
    hasRequirementsError,
    isRequirementsStale,
    requirementsError,
    isLoading
  } = useVaultV2DepositTransaction({
    vaultAddress,
    vaultAsset: vaultAsset.address,
    erc20Amount,
    nativeAmount
  });
  const [builtTransaction, setBuiltTransaction] = useState<typeof transaction>();
  const [isBuildingFlow, setIsBuildingFlow] = useState(false);
  const [flowBuildError, setFlowBuildError] = useState<Error>();
  const {
    simulationAuthorizations,
    simulationApprovalError
  } = tryBuildSimulationAuthorizations({
    requirements,
    chainId: targetChain.id,
    from: account.address
  });
  const simulationKey = ['deposit', erc20Amount.toString(), nativeAmount.toString()];
  const amountKey = `${erc20Amount}:${nativeAmount}`;
  const hasAttemptedCurrentAmount = attemptedAmountKey === amountKey;
  const {
    displayedFinalPosition,
    isUnverifiedFinalPosition,
    isRefreshing: isSimulationRefreshing,
    status: simulationStatus
  } = useVaultV2SimulatedFinalPosition({
    vaultAddress,
    chainId: vaultAsset.chain.id,
    transaction,
    simulationAuthorizations,
    simulationKey,
    fallbackPosition: finalPosition,
    isPreparing: isDepositSimulationPreparing(isLoading, hasAttemptedCurrentAmount)
  });
  const recapSnapshot = useDepositRecapSnapshot(state.flowState === 'idle', userPosition, displayedFinalPosition, isUnverifiedFinalPosition);
  const displayedRecap = recapSnapshot ?? {
    initialPosition: userPosition,
    finalPosition: displayedFinalPosition,
    isSimulationFallback: isUnverifiedFinalPosition
  };
  const walletSupportsAtomicBatching = capability.data ?? false;
  const reviewTrackingParams = buildReviewDialogParams({
    reviewSessionId,
    entryPoint,
    operationTypes: [ACTION_TYPE],
    isSafe: account.isSafe,
    batchesOnchainRequirements: walletSupportsAtomicBatching,
    requirements
  });
  const isPrepared = [!!transaction, requirements !== undefined, !hasRequirementsError, !isRequirementsStale, !!address, !capability.isLoading].every(Boolean);
  const hasFlow = (callRequests?.length ?? 0) > 0;
  const isCurrentFlow = hasFlow && builtTransaction === transaction;
  const currentBuildError = canReplace && builtTransaction === transaction ? flowBuildError : undefined;
  const isBuilding = !hasFlow && (isBuildingFlow || isPrepared);
  const isRefreshing = hasFlow && canReplace && (recapData.loading || isRequirementsStale || isBuildingFlow || isPrepared && !isCurrentFlow);
  const isDisabled = [!recapData.loading && (!recapData.vaultV2 || !recapData.vaultAsset), account.isWrongChain, !transaction, !client, !account.isWalletTypeResolved, requirements === undefined, hasRequirementsError, simulationStatus === 'blacklist', simulationApprovalError !== undefined, capability.isLoading, !hasFlow, !!currentBuildError].some(Boolean);
  const disabledReason = getDepositDisabledReason({
    isWrongChain: account.isWrongChain,
    isBlacklisted: simulationStatus === 'blacklist',
    reviewErrorReason: getReviewErrorReason({
      isTransactionMissing: !transaction && !isLoading,
      isWalletClientMissing: !client && !isClientLoading,
      requirementsError: requirementsError ?? simulationApprovalError ?? currentBuildError
    })
  });
  useEffect(() => {
    let cancelled = false;
    if (!isPrepared || !transaction || !address || !canReplace) {
      setIsBuildingFlow(false);
      return;
    }
    setIsBuildingFlow(true);
    setFlowBuildError(undefined);
    void (async () => {
      try {
        const ownerGeneration = formGeneration.current;
        const trackDepositCompleted = () => {
          trackVaultDepositCompleted({
            vaultAddress,
            vaultVersion: 'v2',
            assetSymbol: vaultAsset.symbol ?? 'UNKNOWN',
            amountRaw: inputAmount.toString(),
            amountUsd: Number(formatUnits(vaultAsset.toUsd(inputAmount) ?? 0n, 18)),
            chainId: targetChain.id,
            chainName: targetChain.name,
            walletType: getWalletType(account.isSafe, account.isContract),
            walletProvider: getWalletProvider(account.connector)
          });
        };
        const nextRequirements = toVaultActionRequirements(requirements ?? []);
        // Same signature vocabulary as the shadow contract: every signature kind the
        // SDK build requires, empty when unsigned.
        const signatureKinds = nextRequirements.filter(isRequirementSignature).map(requirement => requirement.action.type).filter((kind): kind is SdkSignatureKind => SDK_SIGNATURE_KINDS.includes(kind));
        let signedTransaction: ReturnType<typeof transaction.buildTx> | undefined;
        const source = getMorphoSdkActionFlowSource({
          transaction: {
            requirements: nextRequirements,
            buildTx: signatures => {
              signedTransaction = transaction.buildTx(signatures);
              return signedTransaction;
            }
          },
          account: address,
          getRequirementLabel: requirement_0 => formatTransactionRequirement(requirement_0, targetChain.id, vaultAsset).content
        });
        const precedingCalls = source.requirements.map(({
          call
        }) => ({
          ...call,
          value: call.value ?? 0n
        }));
        const nextFlow = await buildActionFlow({
          source,
          account: address,
          chainId: targetChain.id,
          label: 'Deposit',
          tag: ACTION_TYPE,
          transactionLabel: 'Vault V2 Deposit',
          errorAbi: vaultV2Abi,
          walletSupportsAtomicBatching,
          shadow: {
            reviewSessionId,
            walletType: getWalletType(account.isSafe, account.isContract),
            action: {
              flow: 'txVaultV2Deposit',
              vaultAddress,
              account: address,
              assets: inputAmount.toString(),
              slippageBps: DEPOSIT_SLIPPAGE_BPS,
              funding: nativeAmount > 0n && erc20Amount > 0n ? {
                mode: 'auto',
                maxNativeAssets: nativeAmount.toString()
              } : nativeAmount > 0n ? {
                mode: 'native',
                maxNativeAssets: nativeAmount.toString()
              } : {
                mode: 'erc20'
              }
            },
            signatureKinds
          },
          verifyTransaction: async () => {
            setFinalSimulationError(undefined);
            const shouldBypassFinalSimulation = bypassFinalSimulation.current;
            bypassFinalSimulation.current = false;
            if (shouldBypassFinalSimulation) return;
            const transactionToVerify = signedTransaction;
            if (!transactionToVerify) {
              throw new Error('Cannot simulate the deposit before the signed transaction is built');
            }
            try {
              await verifyDeposit({
                buildTx: () => transactionToVerify,
                address,
                chainId: targetChain.id,
                precedingCalls
              });
            } catch (error_0) {
              setFinalSimulationError(error_0);
              throw error_0;
            }
          },
          onSuccess: async result => {
            pendingSyncResult.current = result;
            trackDepositCompleted();
            if (ownerGeneration === formGeneration.current) onTxSuccess();
            try {
              await waitForApiSync(() => refreshAfterDeposit({
                queryClient,
                connectedAddress: connectedAddress.current,
                userAddress: address,
                chainId: targetChain.id,
                vaultAddress,
                vaultAssetAddress: vaultAsset.address,
                refetchPosition,
                refetchVaultData
              }))(result);
            } finally {
              pendingSyncResult.current = undefined;
            }
          }
        });
        if (cancelled) return;
        if (registerActionFlow(nextFlow)) {
          trackDepositCompletedRef.current = trackDepositCompleted;
          setBuiltTransaction(transaction);
        }
      } catch (error) {
        if (cancelled) return;
        const buildError = ensureError(error);
        setFlowBuildError(buildError);
        setBuiltTransaction(transaction);
      } finally {
        if (!cancelled) setIsBuildingFlow(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [address, account.connector, account.isContract, account.isSafe, canReplace, erc20Amount, nativeAmount, registerActionFlow, walletSupportsAtomicBatching, hasRequirementsError, inputAmount, isPrepared, isRequirementsStale, onTxSuccess, refetchPosition, queryClient, refetchVaultData, requirements, reviewSessionId, targetChain.id, targetChain.name, transaction, vaultAddress, vaultAsset]);
  const recap = <SectionErrorBoundary fallback={<ErrorServiceUnavailable minHeight="364px" />}>
      <VaultV2Recap type="vaultV2Deposit" amount={inputAmount} initialPosition={displayedRecap.initialPosition} finalPosition={displayedRecap.finalPosition} vaultAddress={vaultAddress} isFinalPositionLoading={recapSnapshot === undefined && simulationStatus === 'loading'} isSimulationFallback={displayedRecap.isSimulationFallback} source={recapSource} />
    </SectionErrorBoundary>;
  const handleExit = (exit: ActionFlowExit) => {
    formGeneration.current += 1;
    const result_0 = pendingSyncResult.current;
    pendingSyncResult.current = undefined;
    if (result_0) {
      displayTxToast({
        notificationStatus: NotificationStatus.success,
        txHash: result_0.hash,
        chainId: result_0.chainId,
        userIsContract: account.isContract
      });
    }
    onExit(exit);
  };
  return <ActionFlowReviewDialog open operationLabel="Deposit" onExit={handleExit} isBuilding={isBuilding} isRefreshing={isDepositActionRefreshing({
    isRefreshing,
    hasAttemptedCurrentAmount,
    isSimulationRefreshing,
    simulationStatus
  })} isDisabled={isDisabled} disabledReason={disabledReason} reviewTrackingParams={reviewTrackingParams} getTracking={(checkout): ActionFlowTracking => {
    const onConfirmedLate = trackDepositCompletedRef.current;
    return {
      actionType: ACTION_TYPE,
      txSource: txSourceForTracking('sdk'),
      ...getTxFlowTracking(flags, 'vaultV2Deposit'),
      chainName: targetChain.name,
      walletType: getWalletType(account.isSafe, account.isContract),
      walletConnector: getWalletProvider(account.connector),
      vaultAddress,
      checkout,
      getSimulationBypassed: () => userBypassedSimulation.current,
      ...(onConfirmedLate && {
        onConfirmedLate
      })
    };
  }} finalSimulationError={finalSimulationError} onSimulationBypassChange={({
    skipFinalSimulation,
    userBypassed
  }) => {
    setAttemptedAmountKey(amountKey);
    bypassFinalSimulation.current = skipFinalSimulation;
    userBypassedSimulation.current = userBypassed;
  }} recap={recap} />;
}
const ACTION_TYPE = InterfaceOperation.InterfaceOperationType.vaultV2Deposit;
// The SDK deposit is built without `slippageTolerance`, so it applies
// `DEFAULT_SLIPPAGE_TOLERANCE` (WAD); the Actions API takes basis points.
const DEPOSIT_SLIPPAGE_BPS = Number(DEFAULT_SLIPPAGE_TOLERANCE * 10_000n / MathLib.WAD);

// The signature kinds the SDK can emit, inside the shadow contract's vocabulary
// (`signatureKindSchema` minus `'none'` and `'permit2'`, which no SDK action produces).
type SdkSignatureKind = 'permit' | 'permit2SignatureTransfer' | 'authorization';
const SDK_SIGNATURE_KINDS: readonly string[] = ['permit', 'permit2SignatureTransfer', 'authorization'];
const POST_SYNC_REFRESH_TIMEOUT_MS = 5_000;
function getDepositDisabledReason({
  isWrongChain,
  isBlacklisted,
  reviewErrorReason
}: {
  isWrongChain: boolean;
  isBlacklisted: boolean;
  reviewErrorReason: string | undefined;
}) {
  if (isWrongChain) return 'Switch chain';
  if (isBlacklisted) return "This transaction can't be completed due to a simulation error.";
  return reviewErrorReason;
}
type BuiltDepositTransaction = Parameters<typeof toSimulationTx>[1] & {
  action: Parameters<typeof buildSimulationLimits>[0];
};
async function verifyDeposit({
  buildTx,
  address,
  chainId,
  precedingCalls
}: {
  buildTx: () => BuiltDepositTransaction;
  address: Address;
  chainId: number;
  precedingCalls: readonly Parameters<typeof toSimulationTx>[1][];
}) {
  const builtTx = buildTx();
  const transactions = [...precedingCalls.map(call => toSimulationTx(address, call)), toSimulationTx(address, builtTx)];
  await checkSimulation(chainId, transactions, {
    limits: buildSimulationLimits(builtTx.action)
  });
}
interface RefreshAfterDepositParameters {
  queryClient: QueryClient;
  connectedAddress: Address | undefined;
  userAddress: Address;
  chainId: number;
  vaultAddress: Address;
  vaultAssetAddress: Address;
  refetchPosition: () => Promise<unknown>;
  refetchVaultData: () => Promise<unknown>;
}
export async function refreshAfterDeposit({
  queryClient,
  connectedAddress,
  userAddress,
  chainId,
  vaultAddress,
  vaultAssetAddress,
  refetchPosition,
  refetchVaultData
}: RefreshAfterDepositParameters): Promise<void> {
  const refetchType = connectedAddress && isAddressEqual(connectedAddress, userAddress) ? 'active' : 'none';
  const invalidate = (queryKey: QueryKey) => queryClient.invalidateQueries({
    queryKey,
    exact: true,
    refetchType
  }, {
    throwOnError: true
  });
  await waitForDepositRefreshes([refetchVaultData(), refetchPosition(), invalidate(orpc.vaultV2.userPosition.queryOptions({
    input: {
      chainId,
      address: vaultAddress,
      userAddress
    }
  }).queryKey), invalidateUserEarnPortfolio(queryClient, userAddress, refetchType), invalidate(readContractQueryKey({
    address: vaultAssetAddress,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: [userAddress],
    chainId
  })), invalidate(getBalanceQueryKey({
    address: userAddress,
    chainId
  }))]);
}
async function waitForDepositRefreshes(refreshes: Promise<unknown>[]) {
  const startedAt = Date.now();
  const results = await withTimeout(Promise.allSettled(refreshes), POST_SYNC_REFRESH_TIMEOUT_MS).catch(() => null);
  const failedCount = results?.filter(result => result.status === 'rejected').length;
  if (results === null || failedCount) {
    safeSentryBreadcrumb({
      category: 'transaction',
      message: 'ActionFlow deposit refresh',
      level: 'warning',
      data: {
        outcome: results === null ? 'timed_out' : 'failed',
        failedCount,
        elapsedMs: Date.now() - startedAt,
        deadlineMs: POST_SYNC_REFRESH_TIMEOUT_MS
      }
    });
  }
}
function toVaultActionRequirements(requirements: readonly ActionRequirement[]): readonly VaultActionRequirement[] {
  if (!requirements.every(isVaultActionRequirement)) {
    throw new Error('Vault V2 deposit does not support Midnight offer-root requirements');
  }
  return requirements;
}
function isVaultActionRequirement(requirement: ActionRequirement): requirement is VaultActionRequirement {
  return requirement.action.type !== 'midnightOfferRootSignature';
}